import "./styles.scss";

import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";

import { useApp } from "context/app";
import { useReferralProgram } from "context/referral-program";

import Button from "components/Button";
import PageLoader from "components/PageLoader";
import DetailsCard from "components/DetailsCard";
import Table from "components/Table";
import RequestPayoutModal from "./components/RequestPayoutModal";
import DashboardContentLayout from "components/layouts/DashboardContentLayout";
import AnimatedGradient from "components/AnimatedGradient";
import ScrollTopMobile from "components/ScrollTopMobile";

import { ReactComponent as ClipboardIcon } from "assets/icons/new/clipboard.svg";
import partnerImage from "assets/images/partner-manager.jpg";

import useResponsive from "libs/helpers/useResponsive";
import priceRender from "libs/helpers/priceRender";
import gradient_props from "libs/gradient_props";

const mainClass = "referral-programs__details";

const ReferralProgramsDetails = () => {
  const { t } = useTranslation();
  const { min, max } = useResponsive();
  const navigate = useNavigate();
  const { search } = useLocation();

  const { user, websites, theme } = useApp();
  const { data, loading } = useReferralProgram();

  const actions = [
    {
      label: t("Add new website"),
      href: "/websites/new",
      event: () =>
        window.gtag("event", "new_website", {
          click_text: t("Add new website"),
          click_url: `/websites/new`,
        }),
    },
    ...(websites?.length > 0
      ? [
          {
            type: "create_client_task",
            onboarding_button: true,
          },
        ]
      : []),
  ];

  const [copied, setCopied] = useState(false);
  const [request_payout_modal, setRequestPayoutModal] = useState(false);
  const [wallet_view, setWalletView] = useState(false);

  const moneyLabel = (items) => {
    const rows = (items || []).filter((item) => item?.currency && item?.amount);

    if (!rows.length) return priceRender((0).toFixed(2), "pln");

    return rows
      .map(({ amount, currency }) =>
        priceRender(((amount || 0) / 100).toFixed(2), currency),
      )
      .join(", ");
  };

  const walletItems = (data?.wallet || []).filter((item) => item?.amount);
  const hasWallet = walletItems.length > 0;
  const balanceItems = hasWallet
    ? walletItems
    : [{ currency: "pln", amount: 0 }];
  const walletLabel = moneyLabel(walletItems);

  const leadsTable = {
    keys: [
      { key: "full_name", sort_key: false, value: t("Full name") },
      { key: "email", sort_key: false, value: t("Email") },
      { key: "amount", sort_key: false, value: t("Amount") },
      { key: "created_at", sort_key: false, value: t("Date") },
    ],
    items: (data?.leads || []).map((lead) => {
      const name = [lead?.first_name, lead?.last_name]
        .filter(Boolean)
        .join(" ");

      return [
        { key: "full_name", value: name || t("Deleted user") },
        { key: "email", value: lead?.email || "-" },
        { key: "amount", value: moneyLabel(lead?.earned) },
        {
          key: "created_at",
          value: lead?.created_at ? moment(lead.created_at).format("ll") : "-",
        },
      ];
    }),
  };

  const copyHandler = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);

    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  const {
    time: time_blue,
    seed: seed_blue,
    colors: colors_blue,
  } = gradient_props.blue;
  const {
    time: time_green,
    seed: seed_green,
    colors: colors_green,
  } = gradient_props.green;

  const isAdmin = !!(user?.type === "admin");

  return (
    <DashboardContentLayout
      backMobile={
        isAdmin ? () => navigate(`/referral-program${search}`) : undefined
      }
      header={{
        label: isAdmin
          ? `${data?.first_name} ${data?.last_name}`
          : t("Referral program"),
        description: isAdmin
          ? max.sm
            ? t("Referral program")
            : {
                value: t("Referral program"),
                onClick: () => navigate(`/referral-program${search}`),
              }
          : null,
        async: isAdmin,
      }}
      actions={actions}
      loading={loading}
      isAdaptive
    >
      {!!!loading ? (
        <div className={mainClass}>
          <div className={`${mainClass}__columns`}>
            <div className={`${mainClass}__column`}>
              <div
                className={`${mainClass}__wallet`}
                onMouseEnter={hasWallet ? () => setWalletView(true) : undefined}
                onMouseLeave={
                  hasWallet ? () => setWalletView(false) : undefined
                }
              >
                <div className={`${mainClass}__wallet-header`}>
                  <h4>{t("Wallet")}</h4>
                  {!wallet_view && (
                    <div className={`${mainClass}__wallet-badge`}>
                      {t("20% recurring commission")}
                    </div>
                  )}
                </div>
                <div className={`${mainClass}__wallet-content`}>
                  {wallet_view || (hasWallet && max.sm) ? (
                    <div className={`${mainClass}__wallet-content__item`}>
                      <h1>{walletLabel}</h1>
                      <p>
                        {t(
                          "The amount you’ve earned through the affiliate program.",
                        )}
                      </p>
                    </div>
                  ) : (
                    <div className={`${mainClass}__wallet-content__item`}>
                      <h2>{t("Refer Webinity and earn!")}</h2>
                      <p>
                        {t(
                          "Become our partner and earn a monthly commission for as long as the users you refer maintain an active subscription to our service.",
                        )}
                      </p>
                    </div>
                  )}
                </div>
                <div className={`${mainClass}__background`}>
                  {min.sm && (
                    <AnimatedGradient
                      id="wallet-gradient"
                      time={time_blue}
                      seed={seed_blue}
                      colors={colors_blue[theme]}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className={`${mainClass}__column`}>
              <DetailsCard title={t("Referral link")}>
                <div className={`${mainClass}__referral`}>
                  <div className={`${mainClass}__referral-code`}>
                    <input
                      type="text"
                      value={data?.link || t("Link not available")}
                      disabled
                    />
                    {data?.link && (
                      <Button
                        variant={copied ? "success" : ""}
                        onClick={() => copyHandler(data?.link)}
                      >
                        <ClipboardIcon />
                      </Button>
                    )}
                  </div>
                  <span>
                    {t(
                      "Your referral link. Promote Webinity’s services and build a passive income.",
                    )
                      .split(" ")
                      .map((word, index) => {
                        const strongIndexes = [3, 4, 5];

                        return strongIndexes.includes(index) ? (
                          <strong key={index}>
                            {index !== 0 && " "}
                            {word}
                          </strong>
                        ) : (
                          <Fragment key={index}>
                            {index !== 0 && " "}
                            {word}
                          </Fragment>
                        );
                      })}
                  </span>
                </div>
              </DetailsCard>
              <DetailsCard title={t("Wallet")}>
                <div className={`${mainClass}__balance`}>
                  <div className={`${mainClass}__balance-badge`}>
                    {t("20% recurring commission")}
                  </div>
                <div className={`${mainClass}__balance-amounts`}>
                  {balanceItems.map(({ amount, currency }) => (
                    <strong key={currency}>
                      {priceRender(((amount || 0) / 100).toFixed(2), currency)}
                    </strong>
                  ))}
                </div>
                  <p>
                    {t(
                      "The amount you’ve earned through the affiliate program.",
                    )}
                  </p>
                </div>
              </DetailsCard>
            </div>
            {!isAdmin && (
              <div className={`${mainClass}__column`}>
                <div className={`${mainClass}__manager`}>
                  <h4>{t("Any questions?")}</h4>
                  <div className={`${mainClass}__manager-content`}>
                    <div className={`${mainClass}__manager-avatar`}>
                      <img src={partnerImage} alt="" />
                    </div>
                    <h5>Łukasz Pach</h5>
                    <h6>Partner Manager</h6>
                    <p>
                      <a href="tel:+48690888353">+48 690 888 353</a>
                    </p>
                    <p>
                      <a href="mailto:lukasz@webinity.com">
                        lukasz@webinity.com
                      </a>
                    </p>
                  </div>
                  <div className={`${mainClass}__manager-actions`}>
                    <Link to="/contact/">
                      {t(max.sm ? "Contact" : "Get in touch")}
                    </Link>
                    <Link to="/faq/">{t("View FAQ")}</Link>
                  </div>
                  <div
                    className={`${mainClass}__background ${mainClass}__background--green`}
                  >
                    {min.sm && (
                      <AnimatedGradient
                        id="manager-gradient"
                        time={time_green}
                        seed={seed_green}
                        colors={colors_green[theme]}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={`${mainClass}__leads`}>
            <h3>{t("Leads")}</h3>
            <Table data={leadsTable} />
          </div>
          {max.sm && (
            <div className={`${mainClass}__scroll`}>
              <ScrollTopMobile />
            </div>
          )}
        </div>
      ) : (
        <PageLoader variant="panel" />
      )}
      {!!request_payout_modal && (
        <RequestPayoutModal onClose={() => setRequestPayoutModal(false)} />
      )}
    </DashboardContentLayout>
  );
};

export default ReferralProgramsDetails;
